import { FormComponentFactory } from "../FormComponentFactory";
import { Component, ComponentConfig } from "../Component";
import TreeView from "@mui/lab/TreeView";
import TreeItem from "@mui/lab/TreeItem";
import { AddCircleOutlined, RemoveCircleOutlined } from "@mui/icons-material";
import { DataSetSelector } from "datasheets/components/selectors/DataSetSelector";
import { Fragment, useMemo } from "react";
import { useAtomValue } from "jotai";
import { dataCacheAtom } from "datasheets/model/atoms/WorkbookAtoms";
import { datasetsAtom } from "datasheets/model/atoms/WorkbookFocusAtoms";
import {
  useComponentConfigAtom,
  useComponentCustomOrEditConfigAtom,
  useOutputWire,
} from "../state/ComponentAtoms";
import { ColumnSelector } from "datasheets/components/selectors/ColumnSelector";

interface TreeViewComponent extends ComponentConfig {
  datasetId?: string;
  parentKey?: string;
  displayKey?: string;
}
const ComponentName = "Tree";
export function registerTreeViewComponent() {
  FormComponentFactory.addComponent(ComponentName, {
    config: () => {
      return {
        connections: {
          inputs: [],
          outputs: [],
          inputConnectors: {},
          outputConnectors: {},
        },
      };
    },
    component: (config: Component) => {
      return <TreeViewDisplay config={config} />;
    },
    editor: (config: Component) => {
      return <TreeViewEditor componentId={config.id} />;
    },
    fixedOutputs: () => {
      return ["parent", "child"];
    },
  });
  // Add old names for legacy config
  FormComponentFactory.addAlias("TreeView", ComponentName);
}

function TreeViewDisplay({ config }: { config: Component }) {
  const datasets = useAtomValue(datasetsAtom);
  const cache = useAtomValue(dataCacheAtom);
  const [vanillaComponentConfig] = useComponentCustomOrEditConfigAtom(
    config.id
  );
  const componentConfig = vanillaComponentConfig as TreeViewComponent;
  /// Set up handlers for output wiring
  const parentWireKey =
    "connections" in componentConfig &&
    "outputConnectors" in componentConfig.connections
      ? componentConfig.connections.outputConnectors["parent"]
      : undefined;
  const setParentWireValue = useOutputWire(parentWireKey);
  const captureParent = (id: string) => {
    console.log(id, "selected as parent");
    setParentWireValue(id);
  };
  const childWireKey =
    "connections" in componentConfig &&
    "outputConnectors" in componentConfig.connections
      ? componentConfig.connections.outputConnectors["child"]
      : undefined;
  const setChildWireValue = useOutputWire(childWireKey);
  const captureChild = (id: string) => {
    console.log(id, "selected as child");
    setChildWireValue(id);
  };
  const handleNodeSelect = (ev: React.SyntheticEvent, compoundId: string) => {
    if (compoundId.startsWith("parent")) {
      captureParent(compoundId.substring("parent".length));
    } else {
      captureChild(compoundId.substring("child".length));
    }
  };
  // Check if we have enough data to display component
  const datasetId = componentConfig.datasetId;
  if (
    !(
      !!datasetId &&
      !!componentConfig.displayKey &&
      !!componentConfig.parentKey
    )
  ) {
    return <div>Data not found</div>;
  }
  const data = cache[datasetId];
  const childKey = componentConfig.displayKey;
  const parentKey = componentConfig.parentKey;
  ///
  type NodeList = [string, string][];
  let childNode: { [key: string]: NodeList } = {};
  childNode = Object.values(data).reduce((prev, row) => {
    const childVal = row[childKey];
    const parentVal = row[parentKey];
    if (!!childVal && !!parentVal) {
      if (!(parentVal in prev)) {
        prev[parentVal] = [];
      }
      prev[parentVal].push([childVal, childVal]);
    }
    return prev;
  }, childNode);
  const topNodes: NodeList = Object.keys(childNode).map((val) => [val, val]);
  return (
    <TreeView
      key={"Display-" + config.id}
      defaultCollapseIcon={<RemoveCircleOutlined />}
      defaultExpandIcon={<AddCircleOutlined />}
      onNodeSelect={handleNodeSelect}
    >
      {topNodes.map(([id, label]) => {
        const childrenEl = id in childNode ? childNode[id] : undefined;
        const children = !!childrenEl
          ? childrenEl.map(([id, label]) => {
              return (
                <TreeItem
                  key={id}
                  nodeId={"child" + id}
                  label={label}
                ></TreeItem>
              );
            })
          : undefined;
        return (
          <TreeItem key={id} nodeId={"parent" + id} label={label}>
            {children}
          </TreeItem>
        );
      })}
    </TreeView>
  );
}

function TreeViewEditor({ componentId }: { componentId: string }) {
  const [config, setConfig] = useComponentConfigAtom(componentId);
  const componentConfig = config.editConfig as TreeViewComponent;
  const datasetId = componentConfig.datasetId;
  const setDatasetId = (newDatasetId?: string) => {
    setConfig((config: Component) => {
      return {
        ...config,
        ...{
          editConfig: { ...config.editConfig, ...{ datasetId: newDatasetId } },
        },
      };
    });
  };
  const parentKey = useMemo(
    () => componentConfig.parentKey,
    [componentConfig.parentKey]
  );
  const setParentKey = (newParentKey?: string) => {
    setConfig((config: Component) => {
      return {
        ...config,
        ...{
          editConfig: { ...config.editConfig, ...{ parentKey: newParentKey } },
        },
      };
    });
  };
  const displayKey = useMemo(
    () => componentConfig.displayKey,
    [componentConfig.displayKey]
  );
  const setDisplayKey = (displayKey?: string) => {
    setConfig((config: Component) => {
      return {
        ...config,
        ...{
          editConfig: { ...config.editConfig, ...{ displayKey: displayKey } },
        },
      };
    });
  };
  return (
    <Fragment key={"Editor-" + config.id}>
      <div style={{ fontSize: ".5em" }}>{config.id}</div>
      <div>
        <DataSetSelector
          onSelect={setDatasetId}
          title="Parent Dataset"
          selection={datasetId}
        />
      </div>
      <div>
        <ColumnSelector
          title="Parent Key"
          datasetId={datasetId}
          selection={parentKey}
          onChange={(value: string) => setParentKey(value)}
          isSingleSelection={true}
        />
      </div>
      <div>
        <ColumnSelector
          title="Child Key"
          datasetId={datasetId}
          selection={displayKey}
          onChange={(value: string) => setDisplayKey(value)}
          isSingleSelection={true}
        />
      </div>
    </Fragment>
  );
}
